import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  DollarSign, Users, Calendar, CheckCircle, XCircle, Download, 
  Printer, Search, Filter, Plus, ChevronDown, TrendingUp, 
  CreditCard, Building, Smartphone, FileText, ArrowRight,Edit
} from 'lucide-react';
import './PayrollModule.css';

const API_URL = import.meta.env.VITE_API_URL || 'https://www.secs.gismac.co.ke/api';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const PayrollModule = () => {
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showModal, setShowModal] = useState(false);
  const [showPayslip, setShowPayslip] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(MONTHS[new Date().getMonth()]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  const [formData, setFormData] = useState({
    employeeId: '',
    month: MONTHS[new Date().getMonth()],
    year: new Date().getFullYear(),
    basicSalary: '',
    houseAllowance: '',
    transportAllowance: '',
    overtime: '',
    bonus: '',
    otherEarnings: '',
    advanceDeduction: '',
    loanDeduction: '',
    otherDeductions: '',
    paymentMethod: 'Bank Transfer',
    bankAccount: '',
    bankName: '',
    mpesaNumber: ''
  });

  const [calculatedPay, setCalculatedPay] = useState(null);

  const axiosConfig = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

  useEffect(() => {
    fetchPayroll();
    fetchSummary();
    fetchEmployeesWithoutPayroll();
  }, [selectedMonth, selectedYear]);

  const fetchPayroll = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/payroll?month=${selectedMonth}&year=${selectedYear}`, axiosConfig);
      setPayrollRecords(res.data.data || []);
    } catch (err) {
      showMessage('error', 'Failed to fetch payroll records');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await axios.get(`${API_URL}/payroll/summary?month=${selectedMonth}&year=${selectedYear}`, axiosConfig);
      setSummary(res.data.data);
    } catch (err) {
      console.error('Summary fetch error:', err);
    }
  };

  const fetchEmployeesWithoutPayroll = async () => {
    try {
      const res = await axios.get(`${API_URL}/payroll/employees-without-payroll?month=${selectedMonth}&year=${selectedYear}`, axiosConfig);
      setEmployees(res.data.data || []);
    } catch (err) {
      console.error('Employees fetch error:', err);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Recalculate if salary fields change
    if (['basicSalary', 'houseAllowance', 'transportAllowance', 'overtime', 'bonus', 'otherEarnings', 'advanceDeduction', 'loanDeduction', 'otherDeductions'].includes(name)) {
      calculatePreview({ ...formData, [name]: value });
    }
  };

  const calculatePreview = (data) => {
    const basic = parseFloat(data.basicSalary || 0);
    const house = parseFloat(data.houseAllowance || 0);
    const transport = parseFloat(data.transportAllowance || 0);
    const overtime = parseFloat(data.overtime || 0);
    const bonus = parseFloat(data.bonus || 0);
    const otherEarn = parseFloat(data.otherEarnings || 0);
    
    const gross = basic + house + transport + overtime + bonus + otherEarn;
    
            // Kenya SHA calculation (2.75% of gross)
    const sha = Math.round(gross * 0.0275);
    
    // NITA - fixed 50 KSH
        const nita = 50; // National Industrial Training Authority - fixed 50 KSH
    
    // Housing Levy (1.5% of gross)
    const housingLevy = Math.round(gross * 0.015);
    
    // NSSF 2026: Tier 1 (9,000) + Tier 2 (up to 108,000)
    const tier1Limit = 9000;
    const tier2Limit = 108000;
    const rate = 0.06;
    
    let tier1Employee = Math.min(basic, tier1Limit) * rate;
    let tier2Employee = 0;
    if (basic > tier1Limit) {
      const tier2Earnings = Math.min(basic - tier1Limit, tier2Limit - tier1Limit);
      tier2Employee = tier2Earnings * rate;
    }
    const nssfEmployee = Math.round(tier1Employee + tier2Employee);
    
    // Taxable income = Gross - NSSF - Housing Levy
    const taxableIncome = gross - nssfEmployee - housingLevy;
    
    // PAYE calculation
    let paye = 0;
    if (taxableIncome <= 24000) {
      paye = taxableIncome * 0.10;
    } else if (taxableIncome <= 32333) {
      paye = 2400 + (taxableIncome - 24000) * 0.25;
    } else if (taxableIncome <= 500000) {
      paye = 2400 + 2083.25 + (taxableIncome - 32333) * 0.30;
    } else if (taxableIncome <= 800000) {
      paye = 2400 + 2083.25 + 140300.10 + (taxableIncome - 500000) * 0.325;
    } else {
      paye = 2400 + 2083.25 + 140300.10 + 97500 + (taxableIncome - 800000) * 0.35;
    }
    paye = Math.round(paye);
    
    const totalDed = sha + nssfEmployee + nita + housingLevy + paye + 
      parseFloat(data.advanceDeduction || 0) + parseFloat(data.loanDeduction || 0) + parseFloat(data.otherDeductions || 0);
       const netPay = gross - totalDed;  // ADD THIS LINE
    
        setCalculatedPay({
      grossPay: gross,
      sha: sha,
      nssfEmployee: nssfEmployee,
      nita: 50,
      housingLevy: housingLevy,
      paye: paye,
      totalDeductions: totalDed,
      netPay: netPay
    });
  };

    const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = { ...formData, month: selectedMonth, year: selectedYear };
      
      // If editing (selectedPayroll exists), use PUT. Otherwise use POST.
      if (selectedPayroll && selectedPayroll._id) {
        await axios.put(`${API_URL}/payroll/${selectedPayroll._id}`, payload, axiosConfig);
        showMessage('success', 'Payroll updated successfully');
      } else {
        await axios.post(`${API_URL}/payroll/calculate`, payload, axiosConfig);
        showMessage('success', 'Payroll calculated successfully');
      }
      
      setShowModal(false);
      fetchPayroll();
      fetchSummary();
      fetchEmployeesWithoutPayroll();
      resetForm();
      setSelectedPayroll(null);
    } catch (err) {
      showMessage('error', err.response?.data?.message || 'Failed to save payroll');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedPayroll(null);
    setFormData({
      employeeId: '',
      month: selectedMonth,
      year: selectedYear,
      basicSalary: '',
      houseAllowance: '',
      transportAllowance: '',
      overtime: '',
      bonus: '',
      otherEarnings: '',
      advanceDeduction: '',
      loanDeduction: '',
      otherDeductions: '',
      paymentMethod: 'Bank Transfer',
      bankAccount: '',
      bankName: '',
      mpesaNumber: ''
    });
    setCalculatedPay(null);
  };

  const processPayroll = async (id) => {
    try {
      await axios.put(`${API_URL}/payroll/${id}/process`, {}, axiosConfig);
      showMessage('success', 'Payroll processed');
      fetchPayroll();
    } catch (err) {
      showMessage('error', 'Failed to process payroll');
    }
  };

  const markAsPaid = async (id) => {
    try {
      await axios.put(`${API_URL}/payroll/${id}/pay`, {}, axiosConfig);
      showMessage('success', 'Marked as paid');
      fetchPayroll();
    } catch (err) {
      showMessage('error', 'Failed to mark as paid');
    }
  };

  const deletePayroll = async (id) => {
    if (!confirm('Are you sure you want to delete this payroll record?')) return;
    try {
      await axios.delete(`${API_URL}/payroll/${id}`, axiosConfig);
      showMessage('success', 'Payroll deleted');
      fetchPayroll();
      fetchSummary();
    } catch (err) {
      showMessage('error', 'Failed to delete payroll');
    }
  };

  const viewPayslip = (payroll) => {
    setSelectedPayroll(payroll);
    setShowPayslip(true);
  };
    const editPayroll = (payroll) => {
    setSelectedPayroll(payroll);
    setFormData({
      employeeId: payroll.employeeId || '',
      month: payroll.month || selectedMonth,
      year: payroll.year || selectedYear,
      basicSalary: payroll.basicSalary || '',
      houseAllowance: payroll.houseAllowance || '',
      transportAllowance: payroll.transportAllowance || '',
      overtime: payroll.overtime || '',
      bonus: payroll.bonus || '',
      otherEarnings: payroll.otherEarnings || '',
      advanceDeduction: payroll.advanceDeduction || '',
      loanDeduction: payroll.loanDeduction || '',
      otherDeductions: payroll.otherDeductions || '',
      paymentMethod: payroll.paymentMethod || 'Bank Transfer',
      bankAccount: payroll.bankAccount || '',
      bankName: payroll.bankName || '',
      mpesaNumber: payroll.mpesaNumber || ''
    });
    setShowModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(amount || 0);
  };

  const filteredRecords = payrollRecords.filter(r => {
    const matchesSearch = !searchTerm || 
      r.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.employeeNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || r.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid': return '#10b981';
      case 'Processed': return '#3b82f6';
      case 'Draft': return '#f59e0b';
      case 'Cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  return (
    <div className="payroll-container">
      {/* Header */}
      <div className="payroll-header">
        <div>
          <h1><DollarSign size={28} /> Payroll Management</h1>
          <p>Process salaries, deductions, and generate payslips</p>
        </div>
        <div className="payroll-actions">
          <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="month-select">
            {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))} className="year-select">
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
            <Plus size={16} /> Calculate Payroll
          </button>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`alert alert-${message.type}`}>{message.text}</div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="payroll-stats">
          <div className="stat-card">
            <Users size={20} />
            <div>
              <h3>{payrollRecords.length}</h3>
              <p>Total Employees</p>
            </div>
          </div>
          <div className="stat-card highlight">
            <DollarSign size={20} />
            <div>
              <h3>{formatCurrency(summary.totalGrossPay)}</h3>
              <p>Total Gross Pay</p>
            </div>
          </div>
          <div className="stat-card">
            <TrendingUp size={20} />
            <div>
              <h3>{formatCurrency(summary.totalNetPay)}</h3>
              <p>Total Net Pay</p>
            </div>
          </div>
          <div className="stat-card">
            <CreditCard size={20} />
            <div>
              <h3>{formatCurrency(summary.totalDeductions)}</h3>
              <p>Total Deductions</p>
            </div>
          </div>
          <div className="stat-card success">
            <CheckCircle size={20} />
            <div>
              <h3>{formatCurrency(summary.totalPaid)}</h3>
              <p>Total Paid</p>
            </div>
          </div>
          <div className="stat-card warning">
            <Calendar size={20} />
            <div>
              <h3>{formatCurrency(summary.totalPending)}</h3>
              <p>Pending Payment</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="payroll-filters">
        <div className="search-box">
          <Search size={16} />
          <input 
            type="text" 
            placeholder="Search employee..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="all">All Status</option>
          <option value="Draft">Draft</option>
          <option value="Processed">Processed</option>
          <option value="Paid">Paid</option>
          <option value="Cancelled">Cancelled</option>
        </select>
      </div>

      {/* Payroll Table */}
      <div className="payroll-table-container">
        <table className="payroll-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Basic Salary</th>
              <th>Allowances</th>
              <th>Gross Pay</th>
              <th>Deductions</th>
              <th>Net Pay</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.map(record => (
              <tr key={record._id}>
                <td>
                  <div className="employee-info">
                    <strong>{record.employeeName}</strong>
                    <span>{record.employeeNumber}</span>
                  </div>
                </td>
                <td>{formatCurrency(record.basicSalary)}</td>
                <td>{formatCurrency(record.houseAllowance + record.transportAllowance + record.overtime + record.bonus)}</td>
                <td className="gross">{formatCurrency(record.grossPay)}</td>
                <td className="deductions">{formatCurrency(record.totalDeductions)}</td>
                <td className="net">{formatCurrency(record.netPay)}</td>
                <td>
                  <span className="status-badge" style={{ background: getStatusColor(record.status) + '20', color: getStatusColor(record.status) }}>
                    {record.status}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="btn-icon" onClick={() => viewPayslip(record)} title="View Payslip">
                      <FileText size={14} />
                    </button>
                    <button className="btn-icon" onClick={() => editPayroll(record)} title="Edit">
                      <Edit size={14} />
                    </button>    
                    {record.status === 'Draft' && (
                      <button className="btn-icon" onClick={() => processPayroll(record._id)} title="Process">
                        <CheckCircle size={14} />
                      </button>
                    )}
                    {record.status === 'Processed' && (
                      <button className="btn-icon" onClick={() => markAsPaid(record._id)} title="Mark Paid">
                        <DollarSign size={14} />
                      </button>
                    )}
                    <button className="btn-icon danger" onClick={() => deletePayroll(record._id)} title="Delete">
                      <XCircle size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredRecords.length === 0 && (
          <div className="empty-state">
            <Users size={48} />
            <p>No payroll records found for {selectedMonth} {selectedYear}</p>
            <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
              <Plus size={16} /> Create First Payroll
            </button>
          </div>
        )}
      </div>

      {/* Calculate Payroll Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
            <h2><DollarSign size={20} /> {selectedPayroll ? 'Edit Payroll' : 'Calculate Payroll'}</h2>
              <button className="btn-close" onClick={() => setShowModal(false)}><XCircle size={20} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-section">
                  <h3>Employee Details</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Employee *</label>
                      <select name="employeeId" value={formData.employeeId} onChange={handleInputChange} required>
                        <option value="">Select Employee</option>
                        {employees.map(emp => (
                          <option key={emp._id} value={emp._id}>
                            {emp.firstName} {emp.lastName} ({emp.employeeNumber})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Payment Method</label>
                      <select name="paymentMethod" value={formData.paymentMethod} onChange={handleInputChange}>
                        <option value="Bank Transfer">Bank Transfer</option>
                        <option value="MPesa">MPesa</option>
                        <option value="Cash">Cash</option>
                        <option value="Cheque">Cheque</option>
                      </select>
                    </div>
                  </div>
                  {formData.paymentMethod === 'Bank Transfer' && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Bank Name</label>
                        <input type="text" name="bankName" value={formData.bankName} onChange={handleInputChange} placeholder="e.g., Equity Bank" />
                      </div>
                      <div className="form-group">
                        <label>Account Number</label>
                        <input type="text" name="bankAccount" value={formData.bankAccount} onChange={handleInputChange} placeholder="Account number" />
                      </div>
                    </div>
                  )}
                  {formData.paymentMethod === 'MPesa' && (
                    <div className="form-group">
                      <label>MPesa Number</label>
                      <input type="text" name="mpesaNumber" value={formData.mpesaNumber} onChange={handleInputChange} placeholder="07XX XXX XXX" />
                    </div>
                  )}
                </div>

                <div className="form-section">
                  <h3>Earnings</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Basic Salary *</label>
                      <input type="number" name="basicSalary" value={formData.basicSalary} onChange={handleInputChange} required min="0" />
                    </div>
                    <div className="form-group">
                      <label>House Allowance</label>
                      <input type="number" name="houseAllowance" value={formData.houseAllowance} onChange={handleInputChange} min="0" />
                    </div>
                    <div className="form-group">
                      <label>Transport Allowance</label>
                      <input type="number" name="transportAllowance" value={formData.transportAllowance} onChange={handleInputChange} min="0" />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Overtime</label>
                      <input type="number" name="overtime" value={formData.overtime} onChange={handleInputChange} min="0" />
                    </div>
                    <div className="form-group">
                      <label>Bonus</label>
                      <input type="number" name="bonus" value={formData.bonus} onChange={handleInputChange} min="0" />
                    </div>
                    <div className="form-group">
                      <label>Other Earnings</label>
                      <input type="number" name="otherEarnings" value={formData.otherEarnings} onChange={handleInputChange} min="0" />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Deductions</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Advance Deduction</label>
                      <input type="number" name="advanceDeduction" value={formData.advanceDeduction} onChange={handleInputChange} min="0" />
                    </div>
                    <div className="form-group">
                      <label>Loan Deduction</label>
                      <input type="number" name="loanDeduction" value={formData.loanDeduction} onChange={handleInputChange} min="0" />
                    </div>
                    <div className="form-group">
                      <label>Other Deductions</label>
                      <input type="number" name="otherDeductions" value={formData.otherDeductions} onChange={handleInputChange} min="0" />
                    </div>
                  </div>
                </div>

                {/* Live Calculation Preview */}
                {calculatedPay && (
                  <div className="calculation-preview">
                    <h3>Calculation Preview</h3>
                    <div className="calc-row">
                      <span>Gross Pay:</span>
                      <strong className="gross">{formatCurrency(calculatedPay.grossPay)}</strong>
                    </div>
                    <div className="calc-row deduction"><span>SHA (2.75%)</span><span>-{formatCurrency(calculatedPay.sha)}</span></div>
<div className="calc-row deduction"><span>NSSF Employee (Tier I + II)</span><span>-{formatCurrency(calculatedPay.nssfEmployee)}</span></div>
<div className="calc-row deduction"><span>NITA</span><span>-{formatCurrency(calculatedPay.nita)}</span></div>
<div className="calc-row deduction"><span>Housing Levy (1.5%)</span><span>-{formatCurrency(calculatedPay.housingLevy)}</span></div>
<div className="calc-row deduction"><span>PAYE (Tax)</span><span>-{formatCurrency(calculatedPay.paye)}</span></div>
                    <div className="calc-row deduction">
                      <span>Other Deductions:</span>
                      <span>-{formatCurrency(parseFloat(formData.advanceDeduction || 0) + parseFloat(formData.loanDeduction || 0) + parseFloat(formData.otherDeductions || 0))}</span>
                    </div>
                    <div className="calc-row total-deduction">
                      <span>Total Deductions:</span>
                      <span>-{formatCurrency(calculatedPay.totalDeductions)}</span>
                    </div>
                    <div className="calc-row net-pay">
                      <span>Net Pay:</span>
                      <strong>{formatCurrency(calculatedPay.netPay)}</strong>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn btn-primary">
                  {loading ? 'Saving...' : (selectedPayroll ? 'Update Payroll' : 'Calculate Payroll')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payslip Modal */}
      {showPayslip && selectedPayroll && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h2><FileText size={20} /> Payslip</h2>
              <button className="btn-close" onClick={() => setShowPayslip(false)}><XCircle size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="payslip">
                <div className="payslip-header">
                  <h2>GISMAC Security Limited</h2>
                  <p>Payslip for {selectedPayroll.month} {selectedPayroll.year}</p>
                </div>
                 <div className="payslip-employee">
                  <p><strong>Name:</strong> {selectedPayroll.employeeName}</p>
                  <p><strong>Employee No:</strong> {selectedPayroll.employeeNumber}</p>
                  <p><strong>Department:</strong> {selectedPayroll.employeeId?.department || 'N/A'}</p>
                  <p><strong>Position:</strong> {selectedPayroll.position || selectedPayroll.employeeId?.position || 'N/A'}</p>
                </div>
                <div className="payslip-body">
                  <div className="payslip-section">
                    <h4>Earnings</h4>
                    <div className="payslip-row"><span>Basic Salary</span><span>{formatCurrency(selectedPayroll.basicSalary)}</span></div>
                    <div className="payslip-row"><span>House Allowance</span><span>{formatCurrency(selectedPayroll.houseAllowance)}</span></div>
                    <div className="payslip-row"><span>Transport Allowance</span><span>{formatCurrency(selectedPayroll.transportAllowance)}</span></div>
                    <div className="payslip-row"><span>Overtime</span><span>{formatCurrency(selectedPayroll.overtime)}</span></div>
                    <div className="payslip-row"><span>Bonus</span><span>{formatCurrency(selectedPayroll.bonus)}</span></div>
                    <div className="payslip-row"><span>Other Earnings</span><span>{formatCurrency(selectedPayroll.otherEarnings)}</span></div>
                    <div className="payslip-row gross"><span><strong>Gross Pay</strong></span><span><strong>{formatCurrency(selectedPayroll.grossPay)}</strong></span></div>
                  </div>
                  <div className="payslip-section">
                    <h4>Deductions</h4>
                    <div className="payslip-row"><span>SHA (2.75%)</span><span>-{formatCurrency(selectedPayroll.sha)}</span></div>
<div className="payslip-row"><span>NSSF Employee (Tier I + II)</span><span>-{formatCurrency(selectedPayroll.nssfEmployee)}</span></div>
<div className="payslip-row"><span>NITA</span><span>-{formatCurrency(selectedPayroll.nita)}</span></div>
<div className="payslip-row"><span>Housing Levy (1.5%)</span><span>-{formatCurrency(selectedPayroll.housingLevy)}</span></div>
<div className="payslip-row"><span>PAYE (Tax)</span><span>-{formatCurrency(selectedPayroll.paye)}</span></div>
                    <div className="payslip-row"><span>Advance Deduction</span><span>-{formatCurrency(selectedPayroll.advanceDeduction)}</span></div>
                    <div className="payslip-row"><span>Loan Deduction</span><span>-{formatCurrency(selectedPayroll.loanDeduction)}</span></div>
                    <div className="payslip-row"><span>Other Deductions</span><span>-{formatCurrency(selectedPayroll.otherDeductions)}</span></div>
                    <div className="payslip-row total"><span><strong>Total Deductions</strong></span><span><strong>-{formatCurrency(selectedPayroll.totalDeductions)}</strong></span></div>
                  </div>
                </div>
                <div className="payslip-footer">
                  <div className="payslip-row net">
                    <span><strong>NET PAY</strong></span>
                    <span><strong>{formatCurrency(selectedPayroll.netPay)}</strong></span>
                  </div>
                  <p className="payment-method">
                    <strong>Payment Method:</strong> {selectedPayroll.paymentMethod}
                    {selectedPayroll.paymentMethod === 'Bank Transfer' && ` | ${selectedPayroll.bankName} - ${selectedPayroll.bankAccount}`}
                    {selectedPayroll.paymentMethod === 'MPesa' && ` | ${selectedPayroll.mpesaNumber}`}
                  </p>
                  <p className="status">Status: <span style={{ color: getStatusColor(selectedPayroll.status) }}>{selectedPayroll.status}</span></p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowPayslip(false)}>Close</button>
              <button className="btn btn-primary" onClick={() => window.print()}>
                <Printer size={16} /> Print Payslip
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollModule;
